/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import SidebarContent from './SidebarContent';
import LogoFull from '../../images/logo/gitme-logo-white.svg';
import LogoIcon from '../../images/logo/logo-icon.svg';
import * as Icons from '@mui/icons-material';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  isCollapsed,
  setIsCollapsed
}) => {
  const trigger = useRef(null);
  const sidebar = useRef(null);
  const [isHovered, setIsHovered] = React.useState(false);

  const isExpanded = !isCollapsed || isHovered;

  // close on click outside (mobile only)
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed (mobile only)
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <aside
      ref={sidebar}
      onMouseEnter={() => isCollapsed && setIsHovered(true)}
      onMouseLeave={() => isCollapsed && setIsHovered(false)}
      className={`z-[10000] flex h-screen flex-shrink-0 flex-col border-r border-white/5 bg-black transition-all duration-300 ease-out dark:bg-boxdark ${
        isExpanded ? 'w-72.5' : 'w-20'
      } ${isCollapsed && isHovered ? 'shadow-2xl lg:absolute' : ''} ${
        sidebarOpen ? '' : 'hidden lg:flex'
      }`}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b border-white/10 bg-black/40 px-6 backdrop-blur-sm">
        {!isExpanded ? (
          <div className="flex w-full items-center justify-center">
            <img src={LogoIcon} alt="GitMe" className="h-6 w-6" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <img src={LogoFull} alt="GitMe" className="h-7 w-auto" />
            </div>
            <button
              ref={trigger}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-lg p-2 text-bodydark2 transition-all hover:bg-white/10 hover:text-white lg:hidden"
            >
              <svg
                className="h-5 w-5 fill-current"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M15 10l-1.4-1.4L10 12.2 6.4 8.6 5 10l5 5 5-5z" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-scrollbar flex-1 overflow-y-auto overflow-x-hidden">
        <SidebarContent isCollapsed={!isExpanded} />
      </nav>

      {/* Bottom Actions */}
      <div className="flex items-center justify-between gap-2 border-t border-white/5 px-3 py-2">
        {/* Support Button */}
        <button
          onClick={() => (window.location.href = '/settings/support')}
          className="group flex flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-bodydark2 transition-all hover:bg-white/8 hover:text-white"
          title="Support"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 transition-all group-hover:bg-white/10">
            <Icons.SupportAgent fontSize="small" />
          </div>
          {isExpanded && <span className="text-sm font-medium">Support</span>}
        </button>

        {/* Pin/Unpin Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-bodydark2 transition-all hover:bg-white/8 hover:text-white"
          title={isCollapsed ? 'Pin sidebar' : 'Unpin sidebar'}
        >
          {isCollapsed ? (
            <Icons.PushPinOutlined fontSize="small" />
          ) : (
            <Icons.PushPin fontSize="small" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
